import { Injectable } from '@angular/core';
import { User } from '../models/user.model';
import { ITokenStorage, IAuthStorageService } from '@appchezandre/angular-ui';
import { getUserDataFromToken, isJwtExpired } from '../utils/jwt.utils';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/**
 * Service de stockage pour l'authentification avec gestion "Remember me"
 *
 * Architecture:
 * - User général (auth): UUID stocké dans le JWT (claim 'sub')
 * - User LaBase: ID number stocké ici, données spécifiques à l'application
 *
 * Durées de session:
 * - Avec "Remember me": 30 jours (localStorage)
 * - Sans "Remember me": 24 heures (sessionStorage)
 *
 * Pour les opérations nécessitant l'UUID du User général (ex: consentement),
 * utiliser getUserIdFromToken() depuis jwt.utils.ts
 */
@Injectable({
  providedIn: 'root'
})
export class StorageService implements ITokenStorage, IAuthStorageService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly USER_KEY = 'current_user';
  private readonly EXPIRY_KEY = 'auth_expiry';
  private readonly REMEMBER_KEY = 'auth_remember';

  // Durées en millisecondes
  private readonly SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 heures
  private readonly REMEMBER_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 jours

  // Observable pour notifier les changements d'état d'authentification
  private authStateSubject: BehaviorSubject<boolean>;
  public authState$: Observable<boolean>;

  // Observable pour notifier les changements de données utilisateur
  private userChangedSubject: Subject<void>;
  public userChanged$: Observable<void>;

  constructor() {
    // IMPORTANT: Initialiser les Subjects AVANT d'appeler isAuthenticated()
    // car isAuthenticated() peut appeler clearAuth() qui utilise authStateSubject
    this.authStateSubject = new BehaviorSubject<boolean>(false);
    this.authState$ = this.authStateSubject.asObservable();

    this.userChangedSubject = new Subject<void>();
    this.userChanged$ = this.userChangedSubject.asObservable();

    // Maintenant vérifier l'état d'authentification et mettre à jour
    const isAuth = this.isAuthenticated();
    if (isAuth) {
      this.authStateSubject.next(true);
    }
  }

  /**
   * Détermine le storage à utiliser selon rememberMe
   */
  private getStorage(rememberMe?: boolean): Storage {
    // Si rememberMe n'est pas défini, vérifier si un remember existe déjà
    if (rememberMe === undefined) {
      const existingRemember = localStorage.getItem(this.REMEMBER_KEY);
      rememberMe = existingRemember === 'true';
    }
    return rememberMe ? localStorage : sessionStorage;
  }

  /**
   * Sauvegarde le token JWT avec expiration
   * @param token - Token JWT (access token)
   * @param rememberMe - Si true, garde la session 30 jours, sinon 24h
   * @param refreshToken - Refresh token optionnel (pour "Remember Me")
   */
  setToken(token: string, rememberMe: boolean = false, refreshToken?: string): void {
    const storage = this.getStorage(rememberMe);
    const duration = rememberMe ? this.REMEMBER_DURATION : this.SESSION_DURATION;
    const expiryDate = new Date().getTime() + duration;

    storage.setItem(this.TOKEN_KEY, token);
    storage.setItem(this.EXPIRY_KEY, expiryDate.toString());
    storage.setItem(this.REMEMBER_KEY, rememberMe.toString());

    // Stocker le refresh token si fourni (pour remember me)
    if (refreshToken) {
      storage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }

    // Si rememberMe, nettoyer sessionStorage au cas où
    if (rememberMe) {
      sessionStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.EXPIRY_KEY);
      sessionStorage.removeItem(this.REMEMBER_KEY);
      sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
    }

    // Notifier les abonnés du changement d'état (connecté)
    console.log('[StorageService] setToken called, emitting authState: true');
    this.authStateSubject.next(true);

    // Notifier les abonnés du changement de données utilisateur
    this.userChangedSubject.next();
  }

  /**
   * Récupère le refresh token
   * @returns Refresh token ou null si non trouvé
   */
  getRefreshToken(): string | null {
    // Vérifier d'abord localStorage puis sessionStorage
    let refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      refreshToken = sessionStorage.getItem(this.REFRESH_TOKEN_KEY);
    }
    return refreshToken;
  }

  /**
   * Récupère le token JWT s'il n'est pas expiré
   */
  getToken(): string | null {
    // Vérifier d'abord localStorage puis sessionStorage
    let token = localStorage.getItem(this.TOKEN_KEY);
    let expiry = localStorage.getItem(this.EXPIRY_KEY);
    let storage: Storage = localStorage;

    if (!token) {
      token = sessionStorage.getItem(this.TOKEN_KEY);
      expiry = sessionStorage.getItem(this.EXPIRY_KEY);
      storage = sessionStorage;
    }

    if (!token || !expiry) {
      return null;
    }

    // Vérifier l'expiration
    if (this.isTokenExpired()) {
      this.clearAuth();
      return null;
    }

    return token;
  }

  /**
   * Vérifie si le token est expiré
   * Vérifie à la fois l'expiration locale ET l'expiration JWT
   */
  isTokenExpired(): boolean {
    let expiry = localStorage.getItem(this.EXPIRY_KEY);
    if (!expiry) {
      expiry = sessionStorage.getItem(this.EXPIRY_KEY);
    }

    if (!expiry) {
      return true;
    }

    const expiryDate = parseInt(expiry, 10);
    const localExpired = new Date().getTime() > expiryDate;

    // Vérifier aussi l'expiration du JWT lui-même
    const token = localStorage.getItem(this.TOKEN_KEY) || sessionStorage.getItem(this.TOKEN_KEY);
    if (token && isJwtExpired(token)) {
      return true;
    }

    return localExpired;
  }

  /**
   * Récupère la date d'expiration du token
   */
  getTokenExpiry(): Date | null {
    let expiry = localStorage.getItem(this.EXPIRY_KEY);
    if (!expiry) {
      expiry = sessionStorage.getItem(this.EXPIRY_KEY);
    }

    if (!expiry) {
      return null;
    }

    return new Date(parseInt(expiry, 10));
  }

  /**
   * Sauvegarde les données utilisateur
   * Note: Cette méthode est conservée pour compatibilité mais les données
   * sont maintenant stockées dans le token JWT pour plus de sécurité
   */
  setUser(user: User): void {
    const storage = this.getStorage();
    storage.setItem(this.USER_KEY, JSON.stringify(user));
    this.userChangedSubject.next();
  }

  /**
   * Récupère les données utilisateur depuis le token JWT
   * Fusionne avec les données du localStorage pour les champs spécifiques à LaBase
   * (statutcomptId, email_etudiant, etc.) qui peuvent ne pas être dans le token
   */
  getUser(): User | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    // Décoder le token pour extraire les données utilisateur de base
    const userData = getUserDataFromToken(token);

    // Récupérer les données complètes du storage (contient les champs LaBase)
    const storage = this.getStorage();
    const storedUserStr = storage.getItem(this.USER_KEY);

    if (storedUserStr) {
      try {
        const storedUser = JSON.parse(storedUserStr);
        // Fusionner : token a la priorité, mais on garde les champs LaBase du storage
        return {
          ...storedUser,  // Données LaBase complètes (statutcomptId, email_etudiant, etc.)
          ...userData,    // Données du token (nom, prenom, email, etc.) - priorité
        } as User;
      } catch (error) {
        console.error('[StorageService] Error parsing stored user:', error);
      }
    }

    return userData as User | null;
  }

  /**
   * Vérifie si l'utilisateur est connecté et le token n'est pas expiré
   */
  isAuthenticated(): boolean {
    return !!this.getToken() && !this.isTokenExpired();
  }

  /**
   * Vérifie si le mode "Remember me" est activé
   */
  isRememberMeActive(): boolean {
    return localStorage.getItem(this.REMEMBER_KEY) === 'true';
  }

  /**
   * Récupère les données utilisateur (requis par ITokenStorage)
   */
  getUserData(): any {
    return this.getUser();
  }

  /**
   * Sauvegarde les données utilisateur (requis par ITokenStorage)
   */
  setUserData(user: any): void {
    this.setUser(user);
  }

  /**
   * Supprime uniquement le token (requis par ITokenStorage)
   */
  clearToken(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.TOKEN_KEY);
  }

  /**
   * Déconnexion - supprime le token et les données utilisateur
   */
  clearAuth(): void {
    // Nettoyer les deux storages
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.EXPIRY_KEY);
    localStorage.removeItem(this.REMEMBER_KEY);

    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.EXPIRY_KEY);
    sessionStorage.removeItem(this.REMEMBER_KEY);

    // Notifier les abonnés du changement d'état (déconnecté)
    console.log('[StorageService] clearAuth called, emitting authState: false');
    this.authStateSubject.next(false);
  }
}